<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { startCameraStream, stopStream, captureFrame } from "./stream";
  import { lockBodyScroll } from "../utils/scrollLock";

  interface Props {
    onCapture: (blob: Blob) => void | Promise<void>;
    onCancel: () => void;
    onError: (message: string) => void;
  }
  const { onCapture, onCancel, onError }: Props = $props();

  let video = $state<HTMLVideoElement>();
  let stream: MediaStream | null = null;
  let ready = $state(false);
  let busy = $state(false);
  let status = $state("Opening camera...");

  onMount(() => {
    const unlock = lockBodyScroll();
    (async () => {
      try {
        stream = await startCameraStream({ facingMode: "environment" });
        if (!video) throw new Error("Camera preview unavailable.");
        video.srcObject = stream;
        await video.play();
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          ready = true;
          status = "";
        } else {
          status = "Waiting for camera...";
        }
      } catch (err) {
        onError(err instanceof Error ? err.message : "Couldn't open camera.");
      }
    })();

    return unlock;
  });

  onDestroy(() => {
    if (stream) stopStream(stream);
  });

  async function shoot() {
    if (busy || !video || !ready) return;
    busy = true;
    status = "Processing photo...";
    try {
      const blob = await captureFrame(video);
      await onCapture(blob);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Capture failed.");
    } finally {
      busy = false;
      status = ready ? "" : "Waiting for camera...";
    }
  }

  function onCanPlay() {
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;
    ready = true;
    status = "";
  }
</script>

<div class="camera">
  <!-- svelte-ignore a11y_media_has_caption -->
  <video bind:this={video} playsinline muted oncanplay={onCanPlay}></video>
  <div class="viewfinder" aria-hidden="true">
    <span></span><span></span><span></span><span></span>
  </div>
  {#if status}
    <div class="status" role="status" aria-live="polite">{status}</div>
  {/if}
  <div class="controls">
    <button type="button" class="cancel" onclick={onCancel} aria-label="Cancel">Cancel</button>
    <button
      type="button"
      class="shutter"
      onclick={shoot}
      disabled={busy || !ready}
      aria-label="Capture"
    >
      <span class="dot"></span>
    </button>
    <span class="meter" aria-hidden="true">AUTO</span>
  </div>
</div>

<style>
  .camera {
    position: fixed;
    inset: 0;
    /* dvh accounts for iOS Safari's dynamic toolbar; vh is the fallback */
    height: 100vh;
    height: 100dvh;
    background: #000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .camera::after {
    pointer-events: none;
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, transparent 0 49.8%, rgb(95 242 255 / 0.45) 49.8% 50.2%, transparent 50.2%),
      linear-gradient(0deg, transparent 0 49.8%, rgb(95 242 255 / 0.28) 49.8% 50.2%, transparent 50.2%),
      radial-gradient(circle at center, transparent 0 58%, rgb(0 0 0 / 0.42));
    mix-blend-mode: screen;
  }
  video {
    flex: 1 1 0;
    /* min-height:0 lets the video shrink inside the flex container so the
       controls stay visible. Without it iOS Safari sizes the video to its
       intrinsic dimensions and pushes the controls off-screen. */
    min-height: 0;
    width: 100%;
    object-fit: cover;
  }
  .controls {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding: 1rem;
    /* respect iOS home-indicator safe area */
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
    background: linear-gradient(180deg, transparent, rgb(0 0 0 / 0.88) 32%, #000);
    color: #fff;
    flex-shrink: 0;
  }
  .status {
    position: absolute;
    left: 50%;
    bottom: 7rem;
    transform: translateX(-50%);
    border-radius: 999px;
    background: rgb(0 0 0 / 0.72);
    color: #fff;
    padding: 0.5rem 0.875rem;
    font-size: 0.875rem;
  }
  button {
    background: transparent;
    color: #fff;
    border: 0;
    font: inherit;
    padding: 0.5rem 1rem;
  }
  .cancel,
  .meter {
    justify-self: start;
    border: 1px solid rgb(255 255 255 / 0.18);
    border-radius: 999px;
    background: rgb(255 255 255 / 0.08);
    backdrop-filter: blur(16px);
  }
  .meter {
    justify-self: end;
    padding: 0.58rem 0.85rem;
    color: rgb(255 255 255 / 0.7);
    font: 700 0.68rem / 1 ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    letter-spacing: 0.22em;
  }
  .shutter {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    border: 2px solid rgb(255 255 255 / 0.9);
    box-shadow: 0 0 0 8px rgb(255 255 255 / 0.13), 0 0 42px rgb(95 242 255 / 0.35);
    padding: 0;
  }
  .shutter .dot {
    display: block;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    margin: auto;
    background: radial-gradient(circle, #fff 0 48%, #f2d77b 49% 100%);
  }
  .shutter:disabled {
    opacity: 0.5;
  }
  .viewfinder {
    pointer-events: none;
    position: absolute;
    inset: 5.5rem 1.25rem 8.5rem;
    z-index: 1;
    border: 1px solid rgb(255 255 255 / 0.16);
    border-radius: 1.6rem;
    box-shadow: inset 0 0 0 1px rgb(95 242 255 / 0.12), 0 0 55px rgb(0 0 0 / 0.28);
  }
  .viewfinder span {
    position: absolute;
    width: 2.7rem;
    height: 2.7rem;
    border-color: rgb(242 215 123 / 0.9);
  }
  .viewfinder span:nth-child(1) { left: 0.85rem; top: 0.85rem; border-left: 3px solid; border-top: 3px solid; }
  .viewfinder span:nth-child(2) { right: 0.85rem; top: 0.85rem; border-right: 3px solid; border-top: 3px solid; }
  .viewfinder span:nth-child(3) { right: 0.85rem; bottom: 0.85rem; border-right: 3px solid; border-bottom: 3px solid; }
  .viewfinder span:nth-child(4) { left: 0.85rem; bottom: 0.85rem; border-left: 3px solid; border-bottom: 3px solid; }
</style>
